
import { defineComponent, computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ItemFile from './ItemFile.vue';
import ItemPhoto from './ItemPhoto.vue';
import ItemCase from './ItemCase.vue';
import ItemFolder from './ItemFolder.vue';
import ItemVideo from './ItemVideo.vue';
import AppSkeletonMedia from '@/components/skeletons/AppSkeletonMedia.vue';
import { ICase, IFolder, IMixedData } from '@/types/interfaces';
import { MEDIA_TYPES, MIXED_DATA_TYPE, NAVIGATION_PAGES } from '@/types/enums';
import { useDriveStore } from '../../store/driveStore';
import { useMainStore } from '@/store/mainStore';
import SkeletonService from '@/services/SkeletonService';

export default defineComponent({
  components: {
    ItemFile,
    ItemPhoto,
    ItemCase,
    ItemVideo,
    ItemFolder,
    AppSkeletonMedia,
  },
  props: {
    mixedList: { type: Object as () => IMixedData[], required: true },
    row: { type: Boolean, default: false },
    column: { type: Boolean, default: false },
    selector: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ['removeFromSearch'],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();

    const { mixedSkeletons, currentFolderSkeletons } = SkeletonService.getSkeletonInfo();

    const skeletonsAmount = computed(() => {
      if (driveStore.currentFolder?.id) {
        return currentFolderSkeletons[driveStore.currentFolder.id] || 0;
      }
      return mixedSkeletons.value;
    });

    const folderFileAmount = computed(() => (props.loading ? 27 : driveStore.folderFileAmount[driveStore.folderFileAmount.length - 1]));

    const hideDelete = computed(() => route.meta.navigationPage === NAVIGATION_PAGES.DRIVE);
    const isNetwork = computed(() => route.meta.isNetwork);
    const selectedGroupMedia = computed(() => props.mixedList.filter((el) => el?.selected));

    const systemFolders = ref([
      mainStore.userSettings.accountSystemSettings.userCasesFolderId,
      mainStore.userSettings.accountSystemSettings.userUploadsFolderId,
    ]);

    function openFolder(mix: IMixedData) {
      if (props.selector) {
        return;
      }

      driveStore.currentFolder = { name: mix.folder?.name } as IFolder;

      if (isNetwork.value) {
        router.push(`/drive/network/folder/${mix.id}`);
      } else {
        router.push(`/drive/folder/${mix.id}`);
      }
    }

    function openCase(iCase: ICase) {
      if (props.selector) {
        return;
      }

      driveStore.resetCurrentCase();
      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = iCase.photosAmount;
      driveStore.currentCase.filesAmount = iCase.filesAmount;

      if (isNetwork.value) {
        router.push(`/drive/case/network/${iCase.id}/view/`);
      } else {
        router.push(`/drive/case/my/${iCase.id}/view/`);
      }
    }

    function removeFromSearch(id: string) {
      emit('removeFromSearch', id);
    }

    function select(event: Event, mixedItem: IMixedData) {
      if (!props.selector) {
        return;
      }
      if (mixedItem.type === MIXED_DATA_TYPE.FOLDER && systemFolders.value.includes(mixedItem.folder!.id)) {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      mixedItem.selected = !mixedItem.selected;
    }

    return {
      hideDelete,
      skeletonsAmount,
      folderFileAmount,
      openFolder,
      openCase,
      select,
      removeFromSearch,
      MIXED_DATA_TYPE,
      MEDIA_TYPES,
      systemFolders,
      selectedGroupMedia,
    };
  },
});
